/**
 * Centralized Theme Configuration
 *
 * This is the single source of truth for all design tokens.
 * Changing a value here automatically cascades to every CSS variable
 * across the entire application via ThemeProvider.
 *
 * Usage:
 *   - Import `theme` to reference values in JS (e.g. inline styles, charts).
 *   - CSS files use `var(--token-name)` — set by ThemeProvider at runtime.
 *   - To retheme the whole app: edit colors.primary (or any other token) here.
 */

const theme = {
  colors: {
    primary: '#c770f0',
    primaryDark: '#a855d4',
    primaryLight: '#de9ffa',
    secondary: '#68187a',
    accent: '#be50f4',

    bgDeep: '#060410',
    bgDark: '#0d0d1a',
    bgMedium: '#1b1a2e',
    bgCard: 'rgba(255, 255, 255, 0.04)',
    bgCardHover: 'rgba(255, 255, 255, 0.07)',

    textPrimary: '#ffffff',
    textSecondary: 'rgba(255, 255, 255, 0.78)',
    textMuted: '#a588c0',

    borderColor: 'rgba(199, 112, 240, 0.28)',
    borderHover: 'rgba(199, 112, 240, 0.85)',

    scrollbarTrack: '#2d1950',
    scrollbarThumb: 'rgba(178, 121, 216, 0.9)',
    scrollbarThumbHover: 'rgba(222, 130, 235, 0.9)',

    footer: 'rgb(8, 4, 18)',
    socialIconBg: 'rgba(255, 255, 255, 0.97)',
    socialIconHoverBg: '#68187a',
    socialIconHoverGlow: '#801f95',
    socialIconColor: '#700c86',
  },

  gradients: {
    body: 'linear-gradient(135deg, #060410 0%, #120820 50%, #0d0d1a 100%)',
    section: 'linear-gradient(to bottom left, rgba(17, 16, 16, 0.6), rgba(12, 8, 24, 0.9))',
    image: 'linear-gradient(to bottom left, rgba(17, 16, 16, 0.678), rgba(12, 10, 22, 0.863))',
    navbarSticky: 'rgba(27, 26, 46, 0.95)',
    navbarMobile: '#181a27',
  },

  typography: {
    fontFamily: "'Raleway', 'Inter', sans-serif",
    fontSizeBase: '1rem',
    lineHeightBase: '1.65',
    letterSpacingBase: '0.01em',
  },

  spacing: {
    navHeight: '65px',
    sectionPaddingTop: '120px',
    sectionPaddingBottom: '60px',
  },

  borders: {
    radiusXs: '4px',
    radiusSm: '8px',
    radiusMd: '12px',
    radiusLg: '20px',
    radiusXl: '30px',
    radiusFull: '50%',
  },

  shadows: {
    card: '0 4px 20px rgba(119, 53, 136, 0.25)',
    cardHover: '0 8px 35px rgba(119, 53, 136, 0.45)',
    glow: '0 0 20px rgba(199, 112, 240, 0.35)',
    glowStrong: '0 0 30px rgba(199, 112, 240, 0.6)',
    icon: '4px 5px 4px 3px rgba(89, 4, 168, 0.14)',
    navbar: '0 4px 20px rgba(9, 5, 29, 0.3)',
    socialBefore: '0 0 15px #801f95',
    socialHover: '0 0 5px #87209e',
  },

  transitions: {
    fast: '0.15s ease',
    normal: '0.3s ease-out',
    slow: '0.5s ease',
  },
};

export default theme;
